import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { PayLogo } from '../PaymentLogos';
import type {
   BankAccountEntry,
   GcashAccountEntry,
} from '@/api/admin';

// Common Philippine banks. The "Other" sentinel switches the field to a
// free-text input so distributors using a bank not on this list aren't
// blocked. Sorted alphabetically except "Other" anchored at the end.
const PH_BANKS = [
   'AUB (Asia United Bank)',
   'BDO Network Bank',
   'BDO Unibank',
   'BPI (Bank of the Philippine Islands)',
   'China Banking Corp',
   'DBP (Development Bank of the Philippines)',
   'EastWest Bank',
   'Land Bank of the Philippines',
   'Maybank',
   'Metrobank',
   'PNB (Philippine National Bank)',
   'PSBank (Philippine Savings Bank)',
   'RCBC (Rizal Commercial Banking Corp)',
   'Robinsons Bank',
   'Security Bank',
   'Sterling Bank',
   'UnionBank of the Philippines',
   'Other',
] as const;

export type ReceiptAccountKind = 'bank' | 'gcash' | 'maya';

export interface ReceiptAccountFormData {
   onSubmit: (entry:
      | { kind: 'bank'; entry: BankAccountEntry }
      | { kind: 'gcash'; entry: GcashAccountEntry & { provider?: 'gcash' | 'maya' } }
   ) => void;
   /** Offer Maya next to GCash (distributors, Ray 2026-10-27). Saved as an e-wallet with provider 'maya'. */
   allowMaya?: boolean;
   /** Who the account is for, in the intro line. */
   intro?: string;
}

interface Props {
   data: ReceiptAccountFormData;
}

const ReceiptAccountForm = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const [kind, setKind] = useState<ReceiptAccountKind>('bank');

   // Bank fields
   const [bankSelect, setBankSelect] = useState<string>(PH_BANKS[3]); // BPI default
   const [otherBankName, setOtherBankName] = useState('');
   const [bankAccountNumber, setBankAccountNumber] = useState('');
   const [bankAccountName, setBankAccountName] = useState('');

   // GCash fields
   const [gcashNumber, setGcashNumber] = useState('');
   const [gcashName, setGcashName] = useState('');

   const handleSubmit = () => {
      if (kind === 'bank') {
         const bankName = bankSelect === 'Other' ? otherBankName.trim() : bankSelect;
         if (!bankName || !bankAccountNumber.trim() || !bankAccountName.trim()) {
            toast.error('Fill in bank, account number, and account name');
            return;
         }
         data.onSubmit({
            kind: 'bank',
            entry: {
               bankName,
               accountNumber: bankAccountNumber.trim(),
               accountName: bankAccountName.trim(),
            },
         });
      } else {
         const wallet = kind === 'maya' ? 'Maya' : 'GCash';
         if (!gcashNumber.trim() || !gcashName.trim()) {
            toast.error(`Fill in ${wallet} number and name`);
            return;
         }
         data.onSubmit({
            kind: 'gcash',
            entry: { number: gcashNumber.trim(), name: gcashName.trim(), ...(kind === 'maya' ? { provider: 'maya' as const } : {}) },
         });
      }
      handleClose();
   };

   return (
      <Wrapper>
         <Intro>{data.intro ?? 'Add a payment account distributors can send credit-package payments to.'}</Intro>

         <KindRow>
            <KindBtn
               type="button"
               $active={kind === 'bank'}
               onClick={() => setKind('bank')}
            >
               <PayLogo kind="bank" size={20} /> Bank Transfer
            </KindBtn>
            <KindBtn
               type="button"
               $active={kind === 'gcash'}
               onClick={() => setKind('gcash')}
            >
               <PayLogo kind="gcash" size={20} /> GCash
            </KindBtn>
            {data.allowMaya && (
               <KindBtn type="button" $active={kind === 'maya'} onClick={() => setKind('maya')}>
                  <PayLogo kind="maya" size={20} /> Maya
               </KindBtn>
            )}
         </KindRow>

         {kind === 'bank' ? (
            <>
               <Field>
                  <Lbl>Bank *</Lbl>
                  <Select
                     value={bankSelect}
                     onChange={(e) => setBankSelect(e.target.value)}
                  >
                     {PH_BANKS.map((b) => (
                        <option key={b} value={b}>
                           {b}
                        </option>
                     ))}
                  </Select>
               </Field>
               {bankSelect === 'Other' && (
                  <Field>
                     <Lbl>Bank name *</Lbl>
                     <Hint>
                        Type the full name of your bank (or branch identifier).
                     </Hint>
                     <TextInput
                        value={otherBankName}
                        placeholder="e.g. CitySavings Bank"
                        onChange={(e) => setOtherBankName(e.target.value)}
                     />
                  </Field>
               )}
               <Field>
                  <Lbl>Account number *</Lbl>
                  <TextInput
                     value={bankAccountNumber}
                     placeholder="e.g. 1234-5678-90"
                     onChange={(e) => setBankAccountNumber(e.target.value)}
                  />
               </Field>
               <Field>
                  <Lbl>Account name *</Lbl>
                  <TextInput
                     value={bankAccountName}
                     placeholder="e.g. SmartFi Inc."
                     onChange={(e) => setBankAccountName(e.target.value)}
                  />
               </Field>
            </>
         ) : (
            <>
               <Field>
                  <Lbl>{kind === 'maya' ? 'Maya' : 'GCash'} number *</Lbl>
                  <TextInput
                     value={gcashNumber}
                     placeholder="e.g. +63 917 555 1234"
                     onChange={(e) => setGcashNumber(e.target.value)}
                  />
               </Field>
               <Field>
                  <Lbl>Registered name *</Lbl>
                  <TextInput
                     value={gcashName}
                     placeholder="e.g. SmartFi Inc."
                     onChange={(e) => setGcashName(e.target.value)}
                  />
               </Field>
            </>
         )}

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={handleSubmit}>
               <Btn
                  title="Add account"
                  type="normal"
                  size="small"
                  bgcolor={colors.PRIMARY}
                  textcolor={colors.WHITE}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 440px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 12px;
   @media (max-width: 480px) { width: 100%; }
`;

const Intro = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const KindRow = styled.div`
   display: grid;
   grid-auto-flow: column;
   grid-auto-columns: minmax(0, 1fr);
   gap: 8px;
`;

const KindBtn = styled.button<{ $active: boolean }>`
   padding: 10px 12px;
   border-radius: 8px;
   border: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $active }) => ($active ? `${colors.PRIMARY}10` : colors.WHITE)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   font-family: inherit;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   &:hover { border-color: ${colors.PRIMARY}; }
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const Lbl = styled.label`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const Hint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const Select = styled.select`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   background: var(--app-surface);
   color: ${colors.MAIN_DARK};
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const TextInput = styled.input`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

export default ReceiptAccountForm;
